import { createSelector } from "@reduxjs/toolkit";
import type { IProjectPns } from "@schemas/projectSchema";
import type { IConnectedBimSpace } from "@schemas/issueSchema";
import { IRootState } from "../../../../../store";
import { selectConnectedBimSpacesDisabled, selectCurrentOpenIssueModal } from "../../issueSelectors";
import { selectProjectPns } from "../../../../projectSelectors";
import { openapi } from "../../../../../../openapi";
import { selectCurrentProjectId } from "../../../../../global/globalSliceCommon";

export const selectMcbimIsActivated = createSelector(
  [(state: IRootState) => state.adminReducer.project],
  (project) => project?.mcbim?.activated === true,
);

export const selectMcbimSpacesQuery = createSelector(
  [(state: IRootState) => state, selectCurrentProjectId, selectCurrentOpenIssueModal],
  (state, currentProjectId, currentOpenIssueModal) => {
    const projectId = currentOpenIssueModal?.projectId ?? currentProjectId;

    if (projectId == null) {
      return {
        data: [],
        hasLoaded: false,
        isFetching: false,
        isError: false,
        error: null,
      };
    }

    const q = openapi.endpoints.getMcbimSpaces.select({ projectId })(state);

    return {
      data: q.data ?? [],
      hasLoaded: q.status === "fulfilled",
      isFetching: q.status === "pending",
      isError: q.status === "rejected",
      error: q.error ?? null,
    };
  },
);

export const selectMcbimHasNoSpaces = createSelector(
  [selectMcbimIsActivated, selectMcbimSpacesQuery],
  (mcbimIsActivated, spacesQuery) =>
    mcbimIsActivated && spacesQuery.hasLoaded && !spacesQuery.isError && spacesQuery.data.length === 0,
);

const getPnsDisplayValue = (space: { pns?: string | null; pnsDefinition?: IProjectPns[number] | null }) =>
  (space.pnsDefinition?.props?.title ?? space.pns ?? "").trim();

const formatMcbimSpaceTitle = (space: {
  pns?: string | null;
  pnsDefinition?: IProjectPns[number] | null;
  floor?: string | null;
  roomNumber?: string | null;
  roomName?: string | null;
}) =>
  [getPnsDisplayValue(space), space.floor, space.roomNumber, space.roomName]
    .map((value) => (value ?? "").trim())
    .filter(Boolean)
    .join(" | ");

const formatMcbimSpaceSubtitle = (space: { building?: string | null; ifcFile?: string | null }) =>
  [space.building, space.ifcFile]
    .map((value) => (value ?? "").trim())
    .filter(Boolean)
    .join(" | ");

const getMcbimSpaceLookupKeys = (space: { roomNumber?: string | null }) => (space.roomNumber ?? "").trim();

const addPnsDefinitionAndDisplayFields = <
  T extends { pns?: string | null; building?: string | null; ifcFile?: string | null },
>(
  space: T,
  pnsById: Map<string, IProjectPns[number]>,
) => {
  const pns = space.pns ?? null;
  const spaceWithPnsDefinition = {
    ...space,
    pns,
    pnsDefinition: pns != null ? pnsById.get(pns) ?? null : null,
  };

  return {
    ...spaceWithPnsDefinition,
    displayTitle: formatMcbimSpaceTitle(spaceWithPnsDefinition),
    displaySubtitle: formatMcbimSpaceSubtitle(spaceWithPnsDefinition),
  };
};

export const selectMcbimSpacesWithPnsDefinition = createSelector(
  [selectMcbimSpacesQuery, selectProjectPns],
  (spacesQuery, projectPns) => {
    const pnsById = new Map(projectPns.map((pns) => [pns.props.pnsId, pns]));

    return {
      ...spacesQuery,
      data: spacesQuery.data.map((space) => addPnsDefinitionAndDisplayFields(space, pnsById)),
    };
  },
);

const selectSelectedConnectedBimSpacesArg = (_state: IRootState, selectedSpaces: IConnectedBimSpace[]) =>
  selectedSpaces;

const selectSpaceSearchArg = (_state: IRootState, _selectedSpaces: IConnectedBimSpace[], search: string) => search;

export const selectAvailableMcbimSpacesForConnectedBimSpaces = createSelector(
  [selectMcbimSpacesWithPnsDefinition, selectSelectedConnectedBimSpacesArg],
  (spacesQuery, selectedSpaces) => {
    const selectedRoomNumbers = new Set(selectedSpaces.map(getMcbimSpaceLookupKeys).filter(Boolean));

    return spacesQuery.data.filter((space) => !selectedRoomNumbers.has(getMcbimSpaceLookupKeys(space)));
  },
);

export const selectSelectedConnectedBimSpacesWithPnsDefinition = createSelector(
  [selectMcbimSpacesWithPnsDefinition, selectProjectPns, selectSelectedConnectedBimSpacesArg],
  (mcbimSpacesQuery, projectPns, selectedSpaces) => {
    const mcbimSpaceByRoomNumber = new Map(
      mcbimSpacesQuery.data.map((space) => [getMcbimSpaceLookupKeys(space), space]),
    );
    const pnsById = new Map(projectPns.map((pns) => [pns.props.pnsId, pns]));

    return selectedSpaces.map((space) => {
      const mcbimSpace = mcbimSpaceByRoomNumber.get(getMcbimSpaceLookupKeys(space));

      return addPnsDefinitionAndDisplayFields(
        {
          ...space,
          pns: space.pns ?? mcbimSpace?.pns ?? null,
          pnsDefinition: mcbimSpace?.pnsDefinition ?? null,
        },
        pnsById,
      );
    });
  },
);

export const selectFilteredAvailableMcbimSpacesForConnectedBimSpaces = createSelector(
  [selectAvailableMcbimSpacesForConnectedBimSpaces, selectSpaceSearchArg],
  (availableSpaces, search) => {
    const q = search.trim().toLowerCase();

    const matches = availableSpaces.filter((space) => {
      if (q.length === 0) {
        return true;
      }

      return [
        space.roomNumber,
        space.roomName,
        space.floor,
        space.building,
        space.ifcFile,
        space.pns,
        space.pnsDefinition?.props.title,
      ]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(q));
    });

    return matches.slice(0, 20);
  },
);

export const selectMcbimSpacesPickerState = createSelector(
  [
    selectMcbimSpacesWithPnsDefinition,
    selectSelectedConnectedBimSpacesWithPnsDefinition,
    selectAvailableMcbimSpacesForConnectedBimSpaces,
    selectFilteredAvailableMcbimSpacesForConnectedBimSpaces,
  ],
  (spacesQuery, selectedSpaces, availableSpaces, filteredSpaces) => ({
    ...spacesQuery,
    selectedSpaces,
    availableSpaces,
    filteredSpaces,
  }),
);
